import React from 'react';
import {
  Box,
  Button,
  Divider,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/he';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import type { ChatUserModel } from 'types/chat-users/chat-user';
import {
  SCHEDULE_MIN_LEAD_MS,
  buildChatSchedulePresets,
  clampScheduleDayjs,
  formatScheduleDisplayHe,
  getChatScheduleBounds,
  isScheduleInstantAllowed,
  type ChatSchedulePreset,
} from 'utils/chat-composer-schedule';

export type ComposerScheduleMenuCopy = {
  title: string;
  customPickerLabel: string;
  confirm: string;
  unavailable: string;
  outOfRange: string;
  maxHint: string;
};

type ComposerSignatureOverride = {
  label: string;
  disabled: boolean;
  onSend: () => void;
};

type ComposerScheduleMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  chatUser: ChatUserModel | null;
  onSchedule: (at: Date) => void;
  copy: ComposerScheduleMenuCopy;
  /** שליחה חד-פעמית עם/בלי חתימה — מופיע מעל אפשרויות התזמון */
  signatureOverride?: ComposerSignatureOverride | null;
  /** תבנית מאושרת לא כפופה לחלון 24 השעות — מינימום 5 דקות ובלי תקרה */
  unboundedWindow?: boolean;
};

const ComposerScheduleMenu: React.FC<ComposerScheduleMenuProps> = ({
  anchorEl,
  open,
  onClose,
  chatUser,
  onSchedule,
  copy,
  signatureOverride,
  unboundedWindow = false,
}) => {
  const theme = useTheme();
  const bounds = React.useMemo(
    () =>
      unboundedWindow
        ? {
            min: new Date(Date.now() + SCHEDULE_MIN_LEAD_MS),
            max: null,
            hasIncomingAnchor: false,
            schedulingAllowed: true,
          }
        : getChatScheduleBounds(chatUser),
    [chatUser, open, unboundedWindow],
  );
  const presets = React.useMemo(() => buildChatSchedulePresets(bounds), [bounds]);
  const [customValue, setCustomValue] = React.useState<Dayjs | null>(null);

  React.useEffect(() => {
    if (!open) {
      setCustomValue(null);
      return;
    }
    setCustomValue(dayjs(bounds.min));
  }, [open, bounds.min]);

  const handlePickPreset = (preset: ChatSchedulePreset) => {
    onSchedule(preset.at);
    onClose();
  };

  const handleConfirmCustom = () => {
    const clamped = clampScheduleDayjs(customValue, bounds);
    if (!clamped) return;
    const at = clamped.toDate();
    if (!isScheduleInstantAllowed(at, bounds)) return;
    onSchedule(at);
    onClose();
  };

  const customAllowed =
    customValue != null &&
    customValue.isValid() &&
    isScheduleInstantAllowed(customValue.toDate(), bounds);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            mt: -0.75,
            width: 'min(320px, calc(100vw - 24px))',
            borderRadius: 2,
            overflow: 'hidden',
            background:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.92)
                : alpha(theme.palette.common.white, 0.96),
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            boxShadow: theme.shadows[8],
          },
        },
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ScheduleSendOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {copy.title}
          </Typography>
        </Stack>
        {bounds.max ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.35 }}>
            {copy.maxHint.replace('{{max}}', formatScheduleDisplayHe(bounds.max))}
          </Typography>
        ) : null}
      </Box>

      {signatureOverride ? (
        <>
          <Box sx={{ py: 0.25 }}>
            <MenuItem
              dense
              disabled={signatureOverride.disabled}
              onClick={() => {
                if (signatureOverride.disabled) return;
                signatureOverride.onSend();
                onClose();
              }}
              sx={{ py: 0.85, px: 1.5, minHeight: 44, gap: 1 }}
            >
              <DrawOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
              <ListItemText
                primary={signatureOverride.label}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              />
            </MenuItem>
          </Box>
          <Divider />
        </>
      ) : null}

      {!bounds.schedulingAllowed ? (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {copy.unavailable}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ py: 0.25 }}>
            {presets.map((preset) => (
              <MenuItem
                key={preset.id}
                dense
                onClick={() => handlePickPreset(preset)}
                sx={{ py: 0.75, px: 1.5, minHeight: 40 }}
              >
                <ListItemText
                  primary={preset.labelHe}
                  secondary={formatScheduleDisplayHe(preset.at)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))}
          </Box>
          <Divider />
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
              <DateTimePicker
                label={copy.customPickerLabel}
                value={customValue}
                onChange={(v) => setCustomValue(clampScheduleDayjs(v, bounds))}
                ampm={false}
                minDateTime={dayjs(bounds.min)}
                maxDateTime={bounds.max ? dayjs(bounds.max) : undefined}
                disabled={!bounds.schedulingAllowed}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </LocalizationProvider>
            {!customAllowed && customValue ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                {copy.outOfRange}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 1.25, textTransform: 'none', fontWeight: 600 }}
              disabled={!customAllowed}
              onClick={handleConfirmCustom}
            >
              {copy.confirm}
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
};

export default ComposerScheduleMenu;
